import React, { useEffect, useState } from 'react';
import { getSitemapData } from '@kfc-global/react-shared/redux/Actions/AppConfigsActions';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row } from 'react-bootstrap';
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';
import { Link } from 'react-router-dom';
import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import { config } from '@kfc-global/react-shared/config/config.utils';

export const SUPPORTED_SITEMAP_DATA = 'SUPPORTED_SITEMAP_DATA';

const { CAT_CATERING, CAT_STORE, CAT_FOOTER } = {
  CAT_CATERING: 'catering',
  CAT_STORE: 'store',
  CAT_FOOTER: 'footer',
};

const { HOME, MENU, CATERING, CATEGORY, PRODUCT, STORE, FOOTER } = {
  HOME: 'Home',
  MENU: 'Menu',
  CATERING: 'Catering',
  CATEGORY: 'Category',
  PRODUCT: 'Product',
  STORE: 'Store',
  FOOTER: 'Footer',
};

const SITEMAP_TITLES = {
  PAGE_TITLE: () => translateWithI18Next('sitemapPageTitle'),
  HOME: () => translateWithI18Next('home'),
  MENU: () => translateWithI18Next('kfcMenu'),
  CATERING: () => translateWithI18Next('cateringText'),
  STORES: () => translateWithI18Next('stores'),
  OTHER_PAGES: () => translateWithI18Next('otherPages'),
};

const getSitemapHtmlData = (groupedSD, sitemapCategory, sitemapHtml) => {
  const sitemapData = groupedSD?.[sitemapCategory];
  if (sitemapData) {
    sitemapHtml[sitemapCategory] = [];
    sitemapData.forEach(({ name, loc }) => {
      sitemapHtml[sitemapCategory].push({ name, loc });
    });
  }
};

const generateMenuCategoryProductSDs = (type, menuSD, categorySD, productSD, sitemapHtml) => {
  sitemapHtml[type] = [];
  menuSD?.forEach(({ name, loc }) => {
    const menuSitemapHtml = { name, loc, [CATEGORY]: [] };
    sitemapHtml[type].push(menuSitemapHtml);

    categorySD?.forEach(({ name, loc }) => {
      const categorySitemapHtml = { name, loc, [PRODUCT]: [] };
      menuSitemapHtml[CATEGORY].push(categorySitemapHtml);

      productSD
        ?.filter(sd => sd.parentloc === loc)
        .forEach(({ name, loc }) => {
          categorySitemapHtml[PRODUCT].push({ name, loc });
        });
    });
  });
};

const getMenuSitemapHtmlData = (groupedSD, sitemapHtml, supportedSitemapData) => {
  let menuSD = groupedSD?.[MENU]?.filter(m => !m.isCatering);
  let categorySD = groupedSD?.[CATEGORY]?.filter(c => !c.isCatering);
  let productSD = groupedSD?.[PRODUCT]?.filter(p => !p.isCatering);
  generateMenuCategoryProductSDs(MENU, menuSD, categorySD, productSD, sitemapHtml);

  if (supportedSitemapData.includes(CAT_CATERING)) {
    menuSD = groupedSD?.[MENU]?.filter(m => m.isCatering);
    categorySD = groupedSD?.[CATEGORY]?.filter(c => c.isCatering);
    productSD = groupedSD?.[PRODUCT]?.filter(p => p.isCatering);
    generateMenuCategoryProductSDs(CATERING, menuSD, categorySD, productSD, sitemapHtml);
  }
};

const Sitemap = ({ title, sitemapData, sitemapCategory, cssClass = '' }) => (
  <div className={`mt-4 ${cssClass}`}>
    <h4 data-testid={`${sitemapCategory}`}>{title}</h4>
    <div className='pt-1'>
      {sitemapData[sitemapCategory]?.map((item, i) => (
        <div key={`${sitemapCategory}-id-${i}`}>
          <Link to={item.loc}>
            <h6>{item.name}</h6>
          </Link>
        </div>
      ))}
    </div>
  </div>
);

const MenuSitemap = ({ title, sitemapData, sitemapCategory }) => {
  const sitemapHtml = sitemapData?.[sitemapCategory] ?? {};
  return (
    <div className='mt-4'>
      <Link to={sitemapHtml?.[0]?.loc}>
        <h4 data-testid={`${sitemapCategory}`}>{title}</h4>
      </Link>
      <div className='pt-1'>
        {sitemapHtml?.map(menu =>
          menu[CATEGORY]?.map((cat, i) => (
            <div key={`cat-id-${i}`}>
              <Link to={cat.loc}>
                <h6>{cat.name}</h6>
              </Link>
              <ul>
                {cat[PRODUCT]?.map((cat, j) => (
                  <li key={`cat-id-${i}-prod-id-${j}`}>
                    <Link to={cat.loc}>
                      <h6>{cat.name}</h6>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )),
        )}
      </div>
    </div>
  );
};

export const SitemapContainer = () => {
  const dispatch = useDispatch();
  const { sitemapData = [] } = useSelector(({ appConfigsReducer }) => appConfigsReducer) ?? {};
  const sitemapDataStr = JSON.stringify(sitemapData);
  const [sitemapHtml, setSitemapHtml] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(getSitemapData());
  }, [dispatch]);

  useEffect(() => {
    const sitemapHtmlData = {};
    if (!isEmpty(sitemapData)) {
      const groupedSD = groupBy(sitemapData, 'type');

      getSitemapHtmlData(groupedSD, HOME, sitemapHtmlData);

      const supportedSitemapData = config(SUPPORTED_SITEMAP_DATA);
      if (supportedSitemapData.includes(CAT_STORE)) {
        getSitemapHtmlData(groupedSD, STORE, sitemapHtmlData);
      }
      if (supportedSitemapData.includes(CAT_FOOTER)) {
        getSitemapHtmlData(groupedSD, FOOTER, sitemapHtmlData);
      }
      getMenuSitemapHtmlData(groupedSD, sitemapHtmlData, supportedSitemapData);

      setSitemapHtml(sitemapHtmlData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitemapDataStr]);

  return (
    <Container>
      <Row>
        <Col className='mt-4'>
          <h1 data-testid='page-title'>{SITEMAP_TITLES.PAGE_TITLE()}</h1>
        </Col>
      </Row>
      {!isEmpty(sitemapHtml) && (
        <Row data-testid='page-body'>
          <Col sm={6} md={6} lg={4}>
            {!isEmpty(sitemapHtml[HOME]) && (
              <div className='mt-4'>
                {sitemapHtml[HOME]?.map((item, i) => (
                  <div key={`${HOME}-id-${i}`}>
                    <Link to={item.loc}>
                      <h4 data-testid={`${HOME}`}>{item.name}</h4>
                    </Link>
                  </div>
                ))}
              </div>
            )}
            {!isEmpty(sitemapHtml[MENU]) && (
              <MenuSitemap title={SITEMAP_TITLES.MENU()} sitemapData={sitemapHtml} sitemapCategory={MENU} />
            )}
            {!isEmpty(sitemapHtml[CATERING]) && (
              <MenuSitemap title={SITEMAP_TITLES.CATERING()} sitemapData={sitemapHtml} sitemapCategory={CATERING} />
            )}
            {!isEmpty(sitemapHtml[FOOTER]) && (
              <Sitemap
                title={SITEMAP_TITLES.OTHER_PAGES()}
                sitemapData={sitemapHtml}
                sitemapCategory={FOOTER}
                cssClass=' d-none d-sm-block d-md-block d-lg-none'
              />
            )}
          </Col>
          {!isEmpty(sitemapHtml[STORE]) && (
            <Col sm={6} md={6} lg={4}>
              <Sitemap title={SITEMAP_TITLES.STORES()} sitemapData={sitemapHtml} sitemapCategory={STORE} />
            </Col>
          )}
          {!isEmpty(sitemapHtml[FOOTER]) && (
            <Col sm={10} md={6} lg={4} className='d-block d-sm-none d-md-none d-lg-block'>
              <Sitemap title={SITEMAP_TITLES.OTHER_PAGES()} sitemapData={sitemapHtml} sitemapCategory={FOOTER} />
            </Col>
          )}
        </Row>
      )}
    </Container>
  );
};
